import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useState, useCallback, type ReactElement } from 'react';
import { useRouter } from 'next/router';
import { User, ArrowLeft, Heart, Eye, EyeOff, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

export default function IndividualLogin(): ReactElement {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const router = useRouter();

  const checkProfileAndRedirect = useCallback(async (token: string): Promise<void> => {
    try {
      const response = await fetch('/api/user-profile-status', {
        method: 'GET',
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to profile page if user hasn't completed their profile
        if (data.isFirstTime || !data.hasCompletedProfile) {
          router.push('/profile');
        } else {
          router.push('/dashboard');
        }
      } else {
        // If we can't check profile status, redirect to dashboard
        router.push('/dashboard');
      }
    } catch (error) {
      console.error('Error checking profile status:', error);
      // If there's an error, redirect to dashboard
      router.push('/dashboard');
    }
  }, [router]);

  useEffect((): (() => void) => {
    const run = async (): Promise<void> => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        // Check profile status and redirect accordingly
        await checkProfileAndRedirect(data.session.access_token);
      }
    };
    void run();
    const { data: listener } = supabase.auth.onAuthStateChange(async (_e, session) => {
      if (session) {
        await checkProfileAndRedirect(session.access_token);
      }
    });
    return () => listener.subscription.unsubscribe();
  }, [router, checkProfileAndRedirect]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError(error.message);
      setIsLoading(false);
      return;
    }
    if (data?.session) {
      await checkProfileAndRedirect(data.session.access_token);
    }
  }

  return (
    <>
      <Head>
        <title>Individual Login – KEM Discovery</title>
        <meta name="description" content="Start your personal journey of emotional discovery with KEM Discovery's individualized assessment and insights." />
      </Head>
      
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12">
        <div className="w-full max-w-md space-y-6">
          {/* Back Link */}
          <div className="flex items-center text-sm text-muted-foreground">
            <Link href="/" className="flex items-center gap-2 hover:text-foreground transition-colors">
              <ArrowLeft className="size-4" />
              Back to home
            </Link>
          </div>

          {/* Single individual login card */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-purple-50/30 dark:from-gray-900 dark:to-purple-950/30">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto size-16 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white grid place-items-center shadow-lg mb-4">
                <User className="size-8" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Personal Discovery
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Begin your personalized journey of emotional understanding
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    placeholder="your.email@example.com"
                    className="h-11"
                    required 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input 
                      id="password" 
                      type={showPassword ? "text" : "password"}
                      value={password} 
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="h-11 pr-10"
                      required 
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="size-4 text-muted-foreground" />
                      ) : (
                        <Eye className="size-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing In..." : "Begin Your Journey"}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  New to KEM Discovery?{' '}
                  <Link href="/individual/signup" className="text-purple-600 hover:text-purple-500 font-medium underline underline-offset-4">
                    Create Your Account
                  </Link>
                </p>
                
                <p className="text-sm text-muted-foreground">
                  Looking for corporate access?{' '}
                  <Link href="/corporate/login" className="text-blue-600 hover:text-blue-500 font-medium underline underline-offset-4">
                    Corporate Login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Sparkles className="size-3" />
                <span>Personalized Results</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="size-3" />
                <span>100% Private</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

